import { Trans } from '@lingui/react/macro';

import type { TVisitSummary } from '../../../../react-query/useVisitSummaries';
import { useTreatmentsGroupedByStatus } from '../patientStripOdontogram/hooks';

import { SheetTxProceduresInfo } from './SheetTxProceduresInfo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faInfo } from '@fortawesome/free-solid-svg-icons';
import { HoverCard, HoverCardContent, HoverCardTrigger, Popover, PopoverContent, PopoverTrigger } from '@repo/videa-components';

interface Props {
  visitSummary?: TVisitSummary;
}

const SheetTx: React.FC<Props> = ({ visitSummary }) => {
  const treatmentsGroupedByStatus = useTreatmentsGroupedByStatus(visitSummary);

  const scheduledTreatments = [
    ...treatmentsGroupedByStatus.scheduled_today,
    ...treatmentsGroupedByStatus.scheduled_future,
  ];

  return (
    <div className="vai-flex vai-flex-col vai-px-4 ">
      <div className="vai-flex vai-flex-row vai-justify-between vai-items-center vai-h-6 vai-mt-5 vai-mb-1">
        <p className="vai-text-muted-foreground vai-mt-1 vai-mb-1">
          <Trans>Unscheduled</Trans>
        </p>
        <HoverCard>
          <HoverCardTrigger>
            <FontAwesomeIcon icon={faInfo} className="vai-text-muted-foreground" />
          </HoverCardTrigger>
          <HoverCardContent side="left">
            <p>Includes treatments that are unscheduled as of today.</p>
          </HoverCardContent>
        </HoverCard>
      </div>
      <div data-testid="unscheduled-treatments">
        {treatmentsGroupedByStatus.unscheduled.length ? (
          treatmentsGroupedByStatus.unscheduled.map(
            ({ mouthArea, treatments }) => (
              <SheetTxProceduresInfo affectedArea={mouthArea} tx={treatments} />
            ),
          )
        ) : (
          <p className="vai-text-muted-foreground vai-text-sm">
            <Trans>No unscheduled treatments</Trans>
          </p>
        )}
      </div>
      <div className="vai-flex vai-flex-row vai-justify-between vai-items-center vai-h-6 vai-mt-5 vai-mb-1">
        <p className="vai-text-muted-foreground">
          <Trans>Scheduled</Trans>
        </p>
      </div>
      <div data-testid="scheduled-treatments">
        {scheduledTreatments.length ? (
          scheduledTreatments.map(({ mouthArea, treatments }) => (
            <SheetTxProceduresInfo affectedArea={mouthArea} tx={treatments} />
          ))
        ) : (
          <p className="vai-text-muted-foreground vai-text-sm">
            <Trans>No scheduled treatments</Trans>
          </p>
        )}
      </div>
      <div className="vai-flex vai-flex-row vai-justify-between vai-items-center vai-h-6 vai-mt-5 vai-mb-1">
        <p className="vai-text-muted-foreground vai-mt-1 vai-mb-1">
          <Trans>Completed</Trans>
        </p>
        <HoverCard>
          <HoverCardTrigger>
            <FontAwesomeIcon icon={faInfo} className="vai-text-muted-foreground" />
          </HoverCardTrigger>
          <HoverCardContent side="left">
            <p>Includes only treatments that have been completed during and since this visit.</p>
          </HoverCardContent>
        </HoverCard>
      </div>
      <div data-testid="completed-treatments">
        {treatmentsGroupedByStatus.completed.length ? (
          treatmentsGroupedByStatus.completed.map(
            ({ mouthArea, treatments }) => (
              <SheetTxProceduresInfo affectedArea={mouthArea} tx={treatments} />
            ),
          )
        ) : (
          <p className="vai-text-muted-foreground vai-text-sm">
            <Trans>No completed treatments</Trans>
          </p>
        )}
      </div>
    </div>
  );
};

export default SheetTx;
